import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { Tooltip } from '@localagent/ui/components/tooltip';
import type { AgentSession } from '../../types/session-types';
import type { ContextSnapshot, SessionContextSummary } from '../../types/context-types';
import type { TicketDetail } from '@localagent/sdk';
import { ContextUsageBar } from './ContextUsageBar';
import { ContextEntryTree } from './ContextEntryTree';
import { TicketContextPanel } from '../work/TicketContextPanel';
import { useTicketForSession } from '../../hooks/useWorkManagement';
import { holdsIdentityAtom } from '../../stores/auth/atoms';
import { sdkConfiguredAtom } from '../../stores/sdk/atoms';
import { getSDK } from '../../services/sdk-instance';
import { formatPersonaLabel, formatPersonaName } from '../../utils/persona-display';
import { CONTEXT_SESSION_TOOLTIP } from './context-copy';

interface SessionContextPanelProps {
  session: AgentSession;
  sessionId?: string;
  sessionContext: SessionContextSummary | null;
  pinChangeCounter?: number;
}

function formatTokens(tokens: number): string {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
  return String(tokens);
}

function agentLabel(snap: ContextSnapshot, key: string): string {
  return snap.personaName ? formatPersonaLabel(snap.personaName, key) : key;
}

function DisclosureButton({
  testId,
  open,
  onToggle,
  data,
  children,
}: {
  testId: string;
  open: boolean;
  onToggle: () => void;
  data: Record<`data-${string}`, number>;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-expanded={open}
      onClick={onToggle}
      {...data}
      className="flex h-8 w-full items-center justify-between rounded-md border border-border bg-card px-2 text-micro text-foreground hover:bg-accent"
    >
      <span className="truncate">{children}</span>
      <span aria-hidden="true">{open ? '▴' : '▾'}</span>
    </button>
  );
}

export function SessionContextPanel({
  session,
  sessionId,
  sessionContext,
  pinChangeCounter: _pinChangeCounter,
}: SessionContextPanelProps) {
  void _pinChangeCounter;
  const [showTree, setShowTree] = useState(false);
  const [showAgents, setShowAgents] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const { data: mapping } = useTicketForSession(sessionId);
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  const { data: ticketDetail } = useQuery<TicketDetail, Error>({
    queryKey: ['work', 'ticket', mapping?.ticket_id],
    queryFn: () => getSDK().workManagement.getTicket(mapping!.ticket_id),
    enabled: Boolean(isAuthenticated && sdkConfigured && mapping?.ticket_id),
  });

  const pins = session?.session_metadata?.context_pins as Array<{
    item_id: string;
    reason: string;
    estimated_tokens: number;
  }> | undefined;

  const parent = sessionContext?.parentContext ?? null;
  const parentHasUsage = parent?.usagePercent != null && parent?.contextWindowSize != null;

  const allTasks = sessionContext ? Array.from(sessionContext.taskContexts.entries()) : [];
  const activeTasks = allTasks.filter(([, s]) => s.status === 'active');
  const inactiveTasks = allTasks.filter(([, s]) => s.status !== 'active');
  activeTasks.sort((a, b) => a[1].timestamp.localeCompare(b[1].timestamp));
  inactiveTasks.sort((a, b) => a[1].timestamp.localeCompare(b[1].timestamp));

  const compactionCount = sessionContext?.compactionEvents.length ?? 0;
  const pinCount = pins?.length ?? 0;
  const retained = session.context_footprint?.total_tokens ?? 0;
  const spent = sessionContext?.sessionSpendTokens ?? 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-caption font-semibold">Context</h3>
        {(retained > 0 || spent > 0) && (
          <Tooltip label={CONTEXT_SESSION_TOOLTIP} side="bottom">
            <p data-testid="session-memory-stat" className="truncate text-micro text-muted-foreground cursor-help">
              {retained > 0 && <span data-testid="session-memory-tokens">{formatTokens(retained)} retained</span>}
              {retained > 0 && spent > 0 ? ' · ' : ''}
              {spent > 0 && <span data-testid="session-spend-tokens">{formatTokens(spent)} spent</span>}
            </p>
          </Tooltip>
        )}
      </div>

      {parent && (
        <div
          data-testid="context-headline"
          data-total-tokens={parentHasUsage ? String(parent.promptTokens ?? '') : ''}
        >
          <button
            type="button"
            data-testid="context-tree-toggle"
            aria-expanded={showTree}
            onClick={() => setShowTree((v) => !v)}
            className="mb-1 flex w-full items-center gap-1 text-micro font-medium text-muted-foreground hover:text-foreground"
          >
            <span className="inline-block w-2 text-center">{showTree ? '▾' : '▸'}</span>
            In context{parent.personaName ? ` — ${formatPersonaName(parent.personaName)}` : ''}
          </button>
          {parentHasUsage ? (
            <ContextUsageBar
              usagePercent={parent.usagePercent}
              attribution={parent.attribution ?? undefined}
              contextWindowSize={parent.contextWindowSize ?? undefined}
              promptTokens={parent.promptTokens}
            />
          ) : (
            <p className="text-micro text-muted-foreground italic">
              Context usage unavailable for this session
            </p>
          )}
          {showTree && (
            <div className="mt-1.5">
              <ContextEntryTree
                entries={parent.entries}
                supersededEntries={parent.supersededEntries}
                compactedEntries={parent.compactedEntries}
              />
            </div>
          )}
        </div>
      )}

      {allTasks.length > 0 && (
        <div>
          <DisclosureButton
            testId="agents-toggle"
            open={showAgents}
            onToggle={() => setShowAgents((v) => !v)}
            data={{ 'data-active': activeTasks.length, 'data-finished': inactiveTasks.length }}
          >
            Agents · {activeTasks.length} active, {inactiveTasks.length} finished
          </DisclosureButton>
          {showAgents && (
            <div className="mt-1.5 space-y-1">
              {activeTasks.map(([key, snap]) => (
                <div key={key} className="flex items-center gap-2" data-testid="agent-row" data-status="active">
                  <span className="text-micro text-muted-foreground truncate min-w-0 max-w-[140px]" title={agentLabel(snap, key)}>
                    {agentLabel(snap, key)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <ContextUsageBar
                      usagePercent={snap.usagePercent}
                      attribution={snap.attribution ?? undefined}
                      contextWindowSize={snap.contextWindowSize ?? undefined}
                      compact
                    />
                  </div>
                  <span className="shrink-0 text-micro text-success-text">● active</span>
                </div>
              ))}
              {inactiveTasks.map(([key, snap]) => (
                <div key={key} className="flex items-center justify-between text-micro gap-2" data-testid="agent-row" data-status="finished">
                  <span className="text-muted-foreground truncate min-w-0" title={agentLabel(snap, key)}>
                    {agentLabel(snap, key)}
                  </span>
                  <span className="text-muted-foreground whitespace-nowrap shrink-0">
                    {snap.finalUsage
                      ? `used ${formatTokens(snap.finalUsage.totalTokens)} tokens · final context ${formatTokens(snap.finalUsage.contextTotalTokens)} (${Math.round(snap.finalUsage.contextUsagePercent)}%)`
                      : snap.totalTokens != null
                        ? `used ${formatTokens(snap.totalTokens)} tokens`
                        : 'finished'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {(compactionCount > 0 || pinCount > 0) && (
        <div>
          <DisclosureButton
            testId="compaction-pinned-toggle"
            open={showHistory}
            onToggle={() => setShowHistory((v) => !v)}
            data={{ 'data-compactions': compactionCount, 'data-pins': pinCount }}
          >
            Compaction and pinned items
          </DisclosureButton>
          {showHistory && (
            <div className="mt-1.5 space-y-2">
              {sessionContext && compactionCount > 0 && (
                <div data-testid="compaction-history" className="space-y-1">
                  {sessionContext.compactionEvents.map((event, i) => (
                    <div key={i} className="rounded-md bg-warning-subtle px-2.5 py-1 text-micro text-warning-text">
                      <div className="inline-flex items-center gap-1.5">
                        <span className="font-medium">{formatPersonaName(event.personaName)}:</span>
                        <span>{event.messagesSummarized} msgs summarized, {formatTokens(event.tokensReduced)} saved</span>
                      </div>
                      {event.compactedEntries.length > 0 && (
                        <div className="mt-0.5 text-warning-text/80">
                          folded: {event.compactedEntries
                            .map((e) => (e.source_args.path as string) ?? (e.source_args.url as string) ?? e.tool_name)
                            .join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {pins && pinCount > 0 && (
                <div data-testid="pinned-items" className="space-y-0.5">
                  {pins.map((item) => (
                    <div
                      key={item.item_id}
                      className="flex items-center justify-between text-micro px-1 py-0.5 rounded hover:bg-accent/30"
                    >
                      <span className="flex items-center gap-1.5 min-w-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground shrink-0">
                          <line x1="12" y1="17" x2="12" y2="22" /><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
                        </svg>
                        <span className="font-mono text-foreground">{item.item_id}</span>
                        <span className="text-muted-foreground truncate">{item.reason}</span>
                      </span>
                      <span className="text-muted-foreground font-mono shrink-0">{formatTokens(item.estimated_tokens)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Linked Ticket */}
      {ticketDetail ? (
        <TicketContextPanel ticket={ticketDetail.ticket} />
      ) : (
        !parent && (
          <p className="text-micro text-muted-foreground text-center py-4">
            {sessionId ? 'No linked ticket' : 'No active session'}
          </p>
        )
      )}
    </div>
  );
}
